import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const MessagingPanel = ({ isOpen, onClose }) => {
  const { currentUser } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [users, setUsers] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const data = await api.getConversations();
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  }, []);

  // Load users for new chat
  const loadUsers = useCallback(async () => {
    try {
      const data = await api.getMessagingUsers();
      setUsers(data.filter(u => u.id !== currentUser?.id));
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }, [currentUser?.id]);

  // Load messages for selected conversation
  const loadMessages = useCallback(async (conversationId) => {
    try {
      const data = await api.getMessages(conversationId);
      setMessages(data);
      // Mark as read
      await api.markConversationRead(conversationId);
      // Refresh conversations to update unread count
      loadConversations();
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  }, [loadConversations]);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      Promise.all([loadConversations(), loadUsers()]).finally(() => setLoading(false));
    }
  }, [isOpen, loadConversations, loadUsers]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation, loadMessages]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages
  useEffect(() => {
    if (!isOpen || !selectedConversation) return;
    
    const interval = setInterval(() => {
      loadMessages(selectedConversation.id);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isOpen, selectedConversation, loadMessages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;

    setSending(true);
    try {
      const message = await api.sendMessage(selectedConversation.id, newMessage.trim());
      setMessages(prev => [...prev, message]);
      setNewMessage('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStartDirectChat = async (userId) => {
    try {
      const conversation = await api.getOrCreateDirectConversation(userId);
      setSelectedConversation(conversation);
      setShowNewChat(false);
      loadConversations();
    } catch (error) {
      console.error('Failed to start chat:', error);
    }
  };

  const handleCreateGroup = async () => {
    if (!groupName.trim() || selectedUsers.length === 0) {
      alert('Please enter a group name and select at least one participant');
      return;
    }

    try {
      const conversation = await api.createGroupConversation(groupName, selectedUsers);
      setSelectedConversation(conversation);
      setShowNewGroup(false);
      setGroupName('');
      setSelectedUsers([]);
      loadConversations();
    } catch (error) {
      console.error('Failed to create group:', error);
    }
  };

  const getConversationName = (conv) => {
    if (conv.name) return conv.name;
    if (conv.type === 'direct' && conv.participants) {
      const other = conv.participants.find(p => p.id !== currentUser?.id);
      return other?.name || 'Unknown';
    }
    return 'Conversation';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl h-[80vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-blue-600 text-white rounded-t-xl">
          <div className="flex items-center gap-3">
            <span className="text-2xl">💬</span>
            <h2 className="text-xl font-bold">Messages</h2>
          </div>
          <button onClick={onClose} className="text-white hover:bg-blue-700 p-2 rounded-lg">
            ✕
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Conversation List */}
          <div className="w-80 border-r flex flex-col bg-neutral-50">
            <div className="p-3 border-b flex gap-2">
              <button
                onClick={() => { setShowNewChat(true); setShowNewGroup(false); }}
                className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
              >
                + New Chat
              </button>
              <button
                onClick={() => { setShowNewGroup(true); setShowNewChat(false); }}
                className="flex-1 px-3 py-2 bg-neutral-200 text-neutral-700 rounded-lg text-sm hover:bg-neutral-300"
              >
                + Group
              </button>
            </div>

            {/* New Chat User Selection */}
            {showNewChat && (
              <div className="p-3 border-b bg-blue-50">
                <div className="text-sm font-medium mb-2">Start a conversation with:</div>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {users.map(user => (
                    <button
                      key={user.id}
                      onClick={() => handleStartDirectChat(user.id)}
                      className="w-full text-left px-3 py-2 rounded hover:bg-blue-100 text-sm"
                    >
                      <div className="font-medium">{user.name}</div>
                      <div className="text-xs text-neutral-500">{user.role}</div>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowNewChat(false)}
                  className="mt-2 text-xs text-neutral-500 hover:text-neutral-700"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* New Group Creation */}
            {showNewGroup && (
              <div className="p-3 border-b bg-green-50">
                <input
                  type="text"
                  placeholder="Group name..."
                  value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                  className="w-full px-3 py-2 border rounded mb-2 text-sm"
                />
                <div className="text-sm font-medium mb-1">Select participants:</div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {users.map(user => (
                    <label key={user.id} className="flex items-center gap-2 px-2 py-1 hover:bg-green-100 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedUsers([...selectedUsers, user.id]);
                          } else {
                            setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                          }
                        }}
                      />
                      <span className="text-sm">{user.name}</span>
                    </label>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={handleCreateGroup}
                    className="flex-1 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => { setShowNewGroup(false); setSelectedUsers([]); setGroupName(''); }}
                    className="px-3 py-1 text-neutral-500 text-sm hover:bg-neutral-100 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-neutral-500">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="p-4 text-center text-neutral-500">
                  No conversations yet.<br />Start a new chat!
                </div>
              ) : (
                conversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => setSelectedConversation(conv)}
                    className={`w-full text-left px-4 py-3 border-b hover:bg-neutral-100 transition ${
                      selectedConversation?.id === conv.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-sm truncate">{getConversationName(conv)}</div>
                      {conv.unreadCount > 0 && (
                        <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                          {conv.unreadCount}
                        </span>
                      )}
                    </div>
                    {conv.lastMessage && (
                      <div className="text-xs text-neutral-500 truncate mt-1">
                        {conv.lastMessage.senderName}: {conv.lastMessage.content}
                      </div>
                    )}
                    <div className="text-xs text-neutral-400 mt-1">
                      {conv.lastMessage ? formatTime(conv.lastMessage.createdAt) : formatTime(conv.createdAt)}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Message Area */}
          <div className="flex-1 flex flex-col">
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="px-4 py-3 border-b bg-neutral-50">
                  <div className="font-medium">{getConversationName(selectedConversation)}</div>
                  <div className="text-xs text-neutral-500">
                    {selectedConversation.type === 'group' 
                      ? `${selectedConversation.participants?.length || 0} participants`
                      : selectedConversation.participants?.find(p => p.id !== currentUser?.id)?.role || ''
                    }
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-neutral-100">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.senderId === currentUser?.id ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg px-4 py-2 ${
                          msg.senderId === currentUser?.id
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border shadow-sm'
                        }`}
                      >
                        {msg.senderId !== currentUser?.id && (
                          <div className="text-xs font-medium text-blue-600 mb-1">{msg.senderName}</div>
                        )}
                        <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                        <div className={`text-xs mt-1 flex items-center gap-2 ${
                          msg.senderId === currentUser?.id ? 'text-blue-200' : 'text-neutral-400'
                        }`}>
                          <span>{formatTime(msg.createdAt)}</span>
                          {/* Read receipts for own messages */}
                          {msg.senderId === currentUser?.id && (
                            <span className="flex items-center gap-1">
                              {msg.readBy && msg.readBy.length > 0 ? (
                                <span title={msg.readBy.map(r => r.userName).join(', ')}>
                                  ✓✓ <span className="text-[10px]">Read</span>
                                </span>
                              ) : (
                                <span>✓ <span className="text-[10px]">Sent</span></span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t bg-white">
                  <div className="flex gap-2">
                    <input
                      ref={inputRef}
                      type="text"
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={sending}
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {sending ? '...' : 'Send'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-neutral-400">
                <div className="text-center">
                  <div className="text-6xl mb-4">💬</div>
                  <div>Select a conversation or start a new chat</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagingPanel;
